import React, { useState, useEffect } from 'react';
import { DollarSign, Info, Calculator, AlertCircle, CheckCircle, ArrowLeft, PieChart, Target } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../../Components/Header';
import Footer from '../../Components/Footer';
import SimpleKakaoButton from '../../Components/SimpleKakaoButton';
import ShareButton from './ShareButton';

const ROICalculator = () => {
  const [inputs, setInputs] = useState({
    sellingPrice: '',
    profitPerUnit: '',
    otherExpenses: '',
    conversionRate: '',
    visitors: ''
  });

  const [result, setResult] = useState(null);
  const [showTooltip, setShowTooltip] = useState('');

  // 입력값 변경 핸들러
  const handleInputChange = (field, value) => {
    setInputs(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // ROI 계산 함수
  const calculateROI = () => {
    const sellingPrice = parseFloat(inputs.sellingPrice) || 0;
    const profitPerUnit = parseFloat(inputs.profitPerUnit) || sellingPrice; // 입력하지 않으면 판매가로 계산
    const otherExpenses = parseFloat(inputs.otherExpenses) || 0;
    const conversionRate = parseFloat(inputs.conversionRate) || 0;
    const visitors = parseInt(inputs.visitors) || 0;

    if (sellingPrice === 0 || conversionRate === 0 || visitors === 0) return null;

    // 기본 계산
    const conversions = visitors * (conversionRate / 100);
    const revenue = conversions * sellingPrice;
    const totalProfit = conversions * profitPerUnit;
    const netProfit = totalProfit - otherExpenses;

    // ROAS 계산 (광고비 대비 매출)
    const roas = otherExpenses > 0 ? (revenue / otherExpenses) * 100 : 0;

    // ROI 계산 (투자 대비 순수익)
    const roi = otherExpenses > 0 ? (netProfit / otherExpenses) * 100 : 0;

    return {
      visitors,
      conversions: Math.round(conversions),
      conversionRate,
      sellingPrice,
      profitPerUnit,
      revenue,
      totalProfit,
      otherExpenses,
      netProfit,
      roas,
      roi
    };
  };

  // 실시간 계산
  useEffect(() => {
    const calculated = calculateROI();
    setResult(calculated);
  }, [inputs]);

  // 입력 검증
  const isValidInput = () => {
    return inputs.sellingPrice &&
      inputs.conversionRate &&
      inputs.visitors &&
      parseFloat(inputs.sellingPrice) > 0 &&
      parseFloat(inputs.conversionRate) > 0 &&
      parseInt(inputs.visitors) > 0;
  };

  return (
    <div className="bg-white overflow-hidden">
      {/* 배경 장식 요소 */}
      <div className="absolute top-0 right-0 -z-10 opacity-30">
        <svg width="800" height="800" viewBox="0 0 800 800" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="400" cy="400" r="400" fill="url(#radialRed)" fillOpacity="0.3" />
          <defs>
            <radialGradient id="radialRed" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(400 400) rotate(90) scale(400)">
              <stop stopColor="#EF4444" />
              <stop offset="1" stopColor="#EF4444" stopOpacity="0" />
            </radialGradient>
          </defs>
        </svg>
      </div>

      <Header />

      {/* 히어로 섹션 */}
      <section className="pt-32 pb-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-red-50 to-white -z-10"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/tools"
            className="inline-flex items-center text-red-600 hover:text-red-700 mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            전체 도구로 돌아가기
          </Link>

          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center px-4 py-2 bg-red-100 text-red-800 rounded-full text-sm font-medium mb-6">
              <DollarSign className="w-4 h-4 mr-2" />
              ROI(순수익) 계산기
            </div>

            <h1 className="text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 mb-6">
              매출부터 순수익까지 <span className="bg-clip-text text-transparent bg-gradient-to-r from-red-600 to-rose-600">완전 분석</span><br />
              ROAS와 ROI 한번에
            </h1>

            <p className="text-xl text-gray-600 mb-8">
              판매가, 원가, 전환율을 입력하면 매출, ROAS, ROI, 순수익을 한번에 계산합니다.<br />
              실무에서 바로 사용할 수 있는 모든 핵심 지표를 제공합니다.
            </p>
            <div className="flex justify-center mb-8">
              <ShareButton />
            </div>
          </div>
        </div>
      </section>

      {/* 계산기 메인 섹션 */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* 입력 폼 */}
            <div className="bg-white rounded-2xl shadow-lg border border-red-200 p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mr-4">
                  <span className="text-2xl">📊</span>
                </div>
                <h2 className="text-2xl font-bold text-gray-900">비즈니스 데이터 입력</h2>
              </div>

              <div className="space-y-6">
                {/* 방문자 수 */}
                <div>
                  <div className="flex items-center mb-2 relative">
                    <div
                      className="flex items-center cursor-help px-2 py-1 -mx-2 -my-1 rounded hover:bg-gray-50"
                      onMouseEnter={() => setShowTooltip('visitors')}
                      onMouseLeave={() => setShowTooltip('')}
                    >
                      <label className="block text-sm font-medium text-gray-700">
                        방문자 수
                      </label>
                      <Info className="w-4 h-4 ml-2 text-gray-400" />
                    </div>
                    {showTooltip === 'visitors' && (
                      <div className="absolute z-10 left-0 top-8 p-2 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-nowrap pointer-events-none">
                        웹사이트 방문자 수 (월간 또는 일정 기간)
                      </div>
                    )}
                  </div>
                  <input
                    type="number"
                    min="0"
                    placeholder="예: 10000"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    value={inputs.visitors}
                    onChange={(e) => handleInputChange('visitors', e.target.value)}
                  />
                </div>

                {/* 전환율 */}
                <div>
                  <div className="flex items-center mb-2 relative">
                    <div
                      className="flex items-center cursor-help px-2 py-1 -mx-2 -my-1 rounded hover:bg-gray-50"
                      onMouseEnter={() => setShowTooltip('conversionRate')}
                      onMouseLeave={() => setShowTooltip('')}
                    >
                      <label className="block text-sm font-medium text-gray-700">
                        전환율 (%)
                      </label>
                      <Info className="w-4 h-4 ml-2 text-gray-400" />
                    </div>
                    {showTooltip === 'conversionRate' && (
                      <div className="absolute z-10 left-0 top-8 p-2 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-nowrap pointer-events-none">
                        방문자 중 구매로 이어지는 비율
                      </div>
                    )}
                  </div>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    placeholder="예: 2.5"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    value={inputs.conversionRate}
                    onChange={(e) => handleInputChange('conversionRate', e.target.value)}
                  />
                </div>

                {/* 판매가 */}
                <div>
                  <div className="flex items-center mb-2 relative">
                    <div
                      className="flex items-center cursor-help px-2 py-1 -mx-2 -my-1 rounded hover:bg-gray-50"
                      onMouseEnter={() => setShowTooltip('sellingPrice')}
                      onMouseLeave={() => setShowTooltip('')}
                    >
                      <label className="block text-sm font-medium text-gray-700">
                        판매가 (원)
                      </label>
                      <Info className="w-4 h-4 ml-2 text-gray-400" />
                    </div>
                    {showTooltip === 'sellingPrice' && (
                      <div className="absolute z-10 left-0 top-8 p-2 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-nowrap pointer-events-none">
                        상품/서비스의 판매 가격
                      </div>
                    )}
                  </div>
                  <input
                    type="number"
                    min="0"
                    placeholder="예: 50000"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    value={inputs.sellingPrice}
                    onChange={(e) => handleInputChange('sellingPrice', e.target.value)}
                  />
                </div>

                {/* 개당 순이익 */}
                <div>
                  <div className="flex items-center mb-2 relative">
                    <div
                      className="flex items-center cursor-help px-2 py-1 -mx-2 -my-1 rounded hover:bg-gray-50"
                      onMouseEnter={() => setShowTooltip('profitPerUnit')}
                      onMouseLeave={() => setShowTooltip('')}
                    >
                      <label className="block text-sm font-medium text-gray-700">
                        개당 순이익 (원)
                      </label>
                      <Info className="w-4 h-4 ml-2 text-gray-400" />
                    </div>
                    {showTooltip === 'profitPerUnit' && (
                      <div className="absolute z-10 left-0 top-8 p-2 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-nowrap pointer-events-none">
                        상품 1개 판매 시 얻는 순이익 (판매가 - 제조비용)
                      </div>
                    )}
                  </div>
                  <input
                    type="number"
                    min="0"
                    placeholder="예: 40000 (상품 1개당 4만원 이익)"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    value={inputs.profitPerUnit}
                    onChange={(e) => handleInputChange('profitPerUnit', e.target.value)}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    선택사항: 입력하지 않으면 판매가와 동일하게 계산합니다
                  </p>
                </div>

                {/* 기타 비용 */}
                <div>
                  <div className="flex items-center mb-2 relative">
                    <div
                      className="flex items-center cursor-help px-2 py-1 -mx-2 -my-1 rounded hover:bg-gray-50"
                      onMouseEnter={() => setShowTooltip('otherExpenses')}
                      onMouseLeave={() => setShowTooltip('')}
                    >
                      <label className="block text-sm font-medium text-gray-700">
                        기타 비용 (원)
                      </label>
                      <Info className="w-4 h-4 ml-2 text-gray-400" />
                    </div>
                    {showTooltip === 'otherExpenses' && (
                      <div className="absolute z-10 left-0 top-8 p-2 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-nowrap pointer-events-none">
                        광고비, 마케팅비, 운영비 등 추가 투자 비용
                      </div>
                    )}
                  </div>
                  <input
                    type="number"
                    min="0"
                    placeholder="예: 5000000 (선택사항)"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    value={inputs.otherExpenses}
                    onChange={(e) => handleInputChange('otherExpenses', e.target.value)}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    선택사항: 광고비, 마케팅비 등을 입력하면 ROAS/ROI 계산이 가능합니다
                  </p>
                </div>
              </div>
            </div>

            {/* 결과 표시 */}
            <div className="space-y-6">
              {/* 계산 결과 카드 */}
              <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-2xl p-8 border-2 border-red-200">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-red-200 rounded-xl flex items-center justify-center mr-4">
                    <PieChart className="w-6 h-6 text-red-700" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">수익성 분석 결과</h2>
                </div>

                {isValidInput() && result ? (
                  <div className="space-y-6">
                    {/* 핵심 지표 */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white/60 rounded-xl p-4 text-center">
                        <div className="text-3xl font-bold text-red-600 mb-1">
                          {result.revenue.toLocaleString()}원
                        </div>
                        <div className="text-sm text-gray-600">매출</div>
                      </div>
                      <div className="bg-white/60 rounded-xl p-4 text-center">
                        <div className="text-3xl font-bold text-red-600 mb-1">
                          {result.netProfit >= 0 ? '+' : ''}{result.netProfit.toLocaleString()}원
                        </div>
                        <div className="text-sm text-gray-600">순수익</div>
                      </div>
                    </div>

                    {/* ROAS & ROI */}
                    {parseFloat(inputs.otherExpenses) > 0 && (
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-white/60 rounded-xl p-4 text-center">
                          <div className="text-3xl font-bold text-blue-600 mb-1">
                            {result.roas.toFixed(0)}%
                          </div>
                          <div className="text-sm text-gray-600">ROAS</div>
                        </div>
                        <div className="bg-white/60 rounded-xl p-4 text-center">
                          <div className={`text-3xl font-bold mb-1 ${result.roi >= 0 ? 'text-red-600' : 'text-red-600'}`}>
                            {result.roi >= 0 ? '+' : ''}{result.roi.toFixed(0)}%
                          </div>
                          <div className="text-sm text-gray-600">ROI</div>
                        </div>
                      </div>
                    )}

                    {/* 상세 분석 */}
                    <div className="bg-white/60 rounded-xl p-4">
                      <h4 className="font-semibold text-gray-900 mb-3">상세 분석</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">방문자</span>
                          <span className="font-medium">{result.visitors.toLocaleString()}명</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">전환 수</span>
                          <span className="font-medium">{result.conversions.toLocaleString()}건</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">전환율</span>
                          <span className="font-medium">{result.conversionRate}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">평균 주문 금액</span>
                          <span className="font-medium text-red-600">{result.sellingPrice.toLocaleString()}원</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">건당 순이익</span>
                          <span className="font-medium text-red-600">{result.profitPerUnit.toLocaleString()}원</span>
                        </div>
                      </div>
                    </div>

                    {/* 수익 구조 */}
                    <div className="bg-white/60 rounded-xl p-4">
                      <h4 className="font-semibold text-gray-900 mb-3">수익 구조</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">총 매출</span>
                          <span className="font-medium text-blue-600">+{result.revenue.toLocaleString()}원</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">총 순이익</span>
                          <span className="font-medium text-red-600">+{result.totalProfit.toLocaleString()}원</span>
                        </div>
                        {parseFloat(inputs.otherExpenses) > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">기타 비용</span>
                            <span className="font-medium text-red-600">-{result.otherExpenses.toLocaleString()}원</span>
                          </div>
                        )}
                        <div className="flex justify-between border-t border-gray-200 pt-2">
                          <span className="text-gray-600 font-medium">최종 순수익</span>
                          <span className={`font-bold ${result.netProfit >= 0 ? 'text-red-600' : 'text-red-600'}`}>
                            {result.netProfit >= 0 ? '+' : ''}{result.netProfit.toLocaleString()}원
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">
                      {!inputs.visitors ? '방문자 수를 입력해주세요' :
                        !inputs.conversionRate ? '전환율을 입력해주세요' :
                          !inputs.sellingPrice ? '판매가를 입력해주세요' :
                            '올바른 값을 입력해주세요'}
                    </p>
                  </div>
                )}
              </div>

              {/* 해석 가이드 */}
              {isValidInput() && result && (
                <div className="bg-blue-50 rounded-2xl p-6 border border-blue-200">
                  <div className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-blue-600 mr-3 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-blue-900 mb-2">결과 해석</h3>
                      <div className="text-sm text-blue-800 space-y-2">
                        <p>
                          • 방문자 <strong>{result.visitors.toLocaleString()}명</strong>에서
                          전환율 <strong>{result.conversionRate}%</strong>로{' '}
                          <strong>{result.conversions.toLocaleString()}건</strong>의 판매가 발생합니다.
                        </p>
                        <p>
                          • 총 매출 <strong>{result.revenue.toLocaleString()}원</strong>에서
                          최종 순수익은 <strong>{result.netProfit.toLocaleString()}원</strong>입니다.
                        </p>
                        {parseFloat(inputs.otherExpenses) > 0 && (
                          <>
                            <p>
                              • ROAS <strong>{result.roas.toFixed(0)}%</strong>는
                              광고비 1원당 <strong>{(result.roas / 100).toFixed(1)}원</strong>의 매출을 의미합니다.
                            </p>
                            <p>
                              • ROI <strong>{result.roi.toFixed(0)}%</strong>는
                              {result.roi > 100 ? ' 투자 대비 높은' : result.roi > 0 ? ' 투자 대비 양호한' : ' 투자 대비 손실'}
                              {' '}수익률을 보여줍니다.
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* 다른 도구 추천 */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">다른 유용한 도구</h2>
            <p className="text-xl text-gray-600">ROI 분석과 함께 사용하면 좋은 도구들입니다</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl shadow-sm border-2 border-yellow-200 p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center mr-4">
                  <span className="text-2xl">💰</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900">전환율 개선 효과 계산기</h3>
              </div>
              <p className="text-gray-600 mb-6">전환율 개선 시나리오를 미리 계산해보세요.</p>
              <Link
                to="/tools/effect"
                className="block w-full bg-gradient-to-r from-yellow-600 to-yellow-700 text-white py-3 px-4 rounded-xl font-medium hover:shadow-md transition-all text-center"
              >
                개선 효과 계산하기
              </Link>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border-2 border-green-200 p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mr-4">
                  <span className="text-2xl">✅</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900">A/B 테스트 결과 판별기</h3>
              </div>
              <p className="text-gray-600 mb-6">A/B 테스트 결과가 진짜 효과인지 판별합니다.</p>
              <Link
                to="/tools/significance"
                className="block w-full bg-gradient-to-r from-green-600 to-green-700 text-white py-3 px-4 rounded-xl font-medium hover:shadow-md transition-all text-center"
              >
                결과 판별하기
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA 섹션 */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">
            지금 바로 전환율 최적화를 시작하세요
          </h2>
          <p className="text-xl mb-10 text-blue-100">
            SWITCHUP으로 직관적이고 편리하게 A/B 테스트를 실행하고 전환율을 개선하세요
          </p>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 mb-10">
            <div className="grid md:grid-cols-3 gap-6 text-center">
              {[
                { label: "직관적인 인터페이스", description: "복잡한 설정 없이 쉽게 시작" },
                { label: "30일 무료 체험", description: "부담 없이 모든 기능 사용" },
                { label: "즉시 결과 확인", description: "실시간 데이터와 분석 제공" }
              ].map((item, index) => (
                <div key={index}>
                  <div className="text-xl font-bold mb-2">{item.label}</div>
                  <div className="text-blue-100">{item.description}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/freestart"
              className="px-8 py-4 bg-white text-blue-600 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-medium"
            >
              무료로 시작하기
            </Link>
            <Link
              to="/service/ab-test"
              className="px-8 py-4 border-2 border-white text-white rounded-xl hover:bg-white hover:text-blue-600 transition-all duration-300 font-medium"
            >
              SWITCHUP 서비스 보기
            </Link>
          </div>
        </div>
      </section>

      {/* 카카오 문의하기 버튼 */}
      {/* <SimpleKakaoButton
              channelId="_yixgxjn"
              buttonText="카카오로 문의하기"
              mobileText="카카오 문의"
            /> */}
      <Footer />
    </div>
  );
};

export default ROICalculator;